// Escape, and what it means when several things could answer it.

import { useEffect } from "react";

import type { PauseScreen } from "./PauseMenu";

/**
 * **Escape peels one layer, and opens the menu when there is nothing left to
 * peel** (PLAN-UI §28.9's ruling, U7).
 *
 * It only ever opened the menu, which was right while the selection had a
 * popover of its own to shut and wrong the moment U6 deleted it: a player
 * with a piece selected, a card aimed or a run half-drawn pressed Escape to
 * put that away and got the pause menu over the top of it instead. So the
 * key means "never mind" first and "let me out" second — one gesture, two
 * readings, in the order the player is most likely to want them.
 *
 * **`dismiss` decides which layer**, and `dismissable` is only whether there
 * is one; duplicating the order here would be a second definition of what a
 * click on nothing means.
 *
 * An open kit `Popover` consumes Escape in the capture phase and this
 * listener is on `window`, so the orders popover during a muster and the
 * build flyout never reach it — the pair the U6 review paid for.
 *
 * **Closing the menu is the menu's own job**, or two listeners would both
 * answer one key and the second would reopen what the first shut. The key is
 * also refused while another moment already wants the screen — a
 * celebration, a branch pick, a run that has ended — since one thing asks to
 * be read at a time, and those three have no dismiss of their own.
 */
export function useEscapeLayers(
  /** Which pause screen is up, or null for closed. */
  pause: PauseScreen | null,
  /** Something already wants the screen and has no dismiss of its own. */
  modalUp: boolean,
  /** Is there a layer to peel? A fresh boolean each render, which is right. */
  dismissable: boolean,
  dismiss: () => void,
  /** The menu's own setter, so no fresh closure lands in the dependencies. */
  setPause: (screen: PauseScreen | null) => void
): void {
  useEffect(() => {
    if (pause !== null || modalUp) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (dismissable) {
        dismiss();
        return;
      }
      setPause("settings");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pause, modalUp, dismissable, dismiss, setPause]);
}
